import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';

/**
 * Shared query client.
 *
 * Defaults chosen for an internal tool where several people work the same records:
 * data goes stale quickly, and a window regaining focus refetches rather than showing
 * someone else's stale view.
 */
export function createQueryClient(onUnauthenticated?: () => void): QueryClient {
  /**
   * A 401 from anywhere means the session has gone: expired, or the account was
   * deactivated while the tab sat open.
   *
   * Handled once, at the cache, rather than in every screen. Without this, an expired
   * session shows up as a scattering of failed panels and the user is left guessing
   * why nothing loads.
   */
  const handleError = (error: unknown) => {
    if (error instanceof ApiError && error.isUnauthenticated) {
      onUnauthenticated?.();
    }
  };

  return new QueryClient({
    queryCache: new QueryCache({ onError: handleError }),
    mutationCache: new MutationCache({ onError: handleError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        // A 401, a 403 or a 404 will not succeed on a retry; only a transient
        // failure will.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status > 0 && error.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}
